// The upload modal's state machine: the staged file (validated on pick), the
// destination folder, the same-name choice, the comment/approval options, and
// the upload itself with progress and abort-on-close. The modal renders what
// this hook decides. A version is only ever asked for — the new-version target,
// or the user's choice when a document they can see already has the name; a
// plain upload is a document of its own, under its name or the next free one.

import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { extensionOf, fetchFiles, uploadDocument } from '@apis/documents';
import type { IDocument } from '@apis/documents';
import { validateFileName, validateFileSize } from '../../utils/validation';
import { documentsErrorMessage } from '../../utils/errorMessages';
import { nameErrorMessage } from '../../utils/validationMessages';
import { versionAwaitingDecision } from '../../utils/approvalOrder';

const reservedSuffix = /\[\d+\]/; // Genba reserves a "[n]" suffix (e.g. "file[1].png") to disambiguate name collision

interface IUseUploadFlowArgs {
  open: boolean;
  siteId: string;
  initialFolderId?: string;
  newVersionTarget: IDocument | null;
  canApprove: boolean;
  onClose: () => void;
  // Refresh the surfaces behind the modal. Awaited, so the modal can stay up
  // until they're correct. `autoApproveSkipped` when approve-on-upload was
  // requested but the rules refused it — the file landed, it just isn't
  // published. `renamedTo` when its name was taken and it got the next free one.
  onUploaded: (folderId: string, autoApproveSkipped: boolean, renamedTo: string | null) => void | Promise<void>;
}

export function useUploadFlow({
  open, siteId, initialFolderId, newVersionTarget, canApprove, onClose, onUploaded,
}: IUseUploadFlowArgs) {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [finishing, setFinishing] = useState(false); // bytes are in; the listing behind the modal isn't refreshed yet
  const [progress, setProgress] = useState<number | null>(null); // 0–1 while bytes are in flight; null when the length isn't computable
  const [error, setError] = useState<string | null>(null);
  const [selectedFolderId, setSelectedFolderId] = useState(newVersionTarget?.folderId ?? initialFolderId ?? '');
  const [comment, setComment] = useState('');
  const [submitForApproval, setSubmitForApproval] = useState(true);
  const [autoApprove, setAutoApprove] = useState(false);
  // The visible document already using the picked file's name, while the user
  // chooses: a new version of it, or a separate document.
  const [sameNamed, setSameNamed] = useState<IDocument | null>(null);

  // Closing the modal (×, Cancel, Esc, backdrop, route change) cancels an in-flight upload
  const abortRef = useRef<AbortController | null>(null);
  useEffect(() => () => abortRef.current?.abort(), []);

  // The destination's listing, to find a document the user can see under the
  // picked name. Shares useFiles' query key, so a viewed folder resolves from
  // cache. Not needed in new-version mode — the target is already explicit.
  const { data: existing, isError: checkFailed } = useQuery({
    queryKey: ['documents', 'files', siteId, selectedFolderId],
    queryFn: ({ signal }) => fetchFiles(siteId, selectedFolderId, signal),
    enabled: open && !!selectedFolderId && !!file && !newVersionTarget,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
  const existingMatch = file ? existing?.documents.find((d) => d.name === file.name) : undefined;
  // Upload waits for the answer, so a fast click can't skip the choice. A failed
  // check doesn't block: the server gives a taken name the next free one.
  const checkingExisting = !newVersionTarget && !!file && !existing && !checkFailed;

  // A new version must be the same type of file as its document.
  const requiredType = newVersionTarget ? extensionOf(newVersionTarget.name) : '';

  const sizeError = file && selectedFolderId ? validateFileSize(selectedFolderId, file.size) : null;

  // A new version's document can have an earlier one still awaiting a
  // decision; a new document has no history to block on. A same-named
  // document doesn't count: the user may yet upload beside it — and if they
  // choose its new version instead, the server refuses the approval and the
  // caller says so.
  const awaitingDecision = newVersionTarget ? versionAwaitingDecision(newVersionTarget.versions) : null;
  const canAutoApprove = canApprove && submitForApproval && awaitingDecision === null;

  /**
   * Both the file input and the drop zone land here, hence the FileList; only
   * the first entry is used. Vets the name up front so a name the store would
   * reject is reported at the picker, and clears the selection when it fails —
   * a staged file that can never be sent is worse than none.
   */
  function acceptPickedFile(files: FileList | null) {
    if (!files || files.length === 0) return;
    const chosen = files[0]!;
    setSameNamed(null);
    // In new-version mode the version is stored under the document's name, so
    // the picked file's own name matters only for its type.
    if (newVersionTarget && extensionOf(chosen.name).toLowerCase() !== requiredType.toLowerCase()) {
      setFile(null);
      setError(t('documents.upload.wrongType', { name: newVersionTarget.name }));
      return;
    }
    if (!newVersionTarget) {
      if (reservedSuffix.test(chosen.name)) {
        setFile(null);
        setError(t('documents.upload.reservedName'));
        return;
      }
      const nameError = validateFileName(chosen.name);
      if (nameError) {
        setFile(null);
        setError(nameErrorMessage(t, nameError));
        return;
      }
    }
    setFile(chosen);
    setError(null);
  }

  function clearFile() {
    setFile(null);
    setError(null);
    setSameNamed(null);
  }

  function handleConfirm() {
    if (!file || !selectedFolderId || checkingExisting || sizeError) return;
    // The name is taken by a document the user can see: they choose.
    if (existingMatch && !newVersionTarget) {
      setSameNamed(existingMatch);
      return;
    }
    void doUpload(newVersionTarget);
  }

  function handleClose() {
    abortRef.current?.abort();
    onClose();
  }

  /** `versionOf`: the document the upload becomes a new version of, or null for
   *  a document of its own. */
  async function doUpload(versionOf: IDocument | null) {
    if (!file || !selectedFolderId) return;
    const controller = new AbortController();
    abortRef.current = controller;
    setUploading(true);
    setProgress(null);
    setError(null);
    try {
      // A file not versioned yet has no documentId: it is named by its own id.
      const target = versionOf?.documentId
        ? { documentId: versionOf.documentId }
        : versionOf ? { versionOf: versionOf.id } : {};
      const approve = canAutoApprove && autoApprove;
      const result = await uploadDocument(siteId, selectedFolderId, file, {
        ...(comment.trim() ? { comment: comment.trim() } : {}),
        ...target,
        submitForApproval,
        approve,
        onProgress: setProgress,
        signal: controller.signal,
      });
      // The bytes are stored, but the table and drawer still show the listing
      // from before. Hold the modal — relabelled — until the refresh lands, so
      // the file is already there the moment it disappears.
      setFinishing(true);
      setProgress(null);
      // A version takes its document's name; only a plain upload can be renamed.
      const renamedTo = !versionOf && result.name !== file.name ? result.name : null;
      await onUploaded(selectedFolderId, approve && !result.approved, renamedTo);
      onClose();
    } catch (err) {
      if (!(err instanceof DOMException && err.name === 'AbortError')) {
        setError(documentsErrorMessage(t, err, { fallbackKey: 'documents.upload.error', sentName: true }));
      }
    } finally {
      abortRef.current = null;
      setUploading(false);
      setFinishing(false);
      setProgress(null);
    }
  }

  return {
    file, uploading, finishing, progress, error,
    selectedFolderId, setSelectedFolderId,
    comment, setComment,
    submitForApproval, setSubmitForApproval,
    autoApprove, setAutoApprove, awaitingDecision,
    sameNamed,
    uploadAsVersion: () => void doUpload(sameNamed),
    uploadSeparately: () => void doUpload(null),
    cancelSameName: () => setSameNamed(null),
    checkingExisting, sizeError, requiredType,
    acceptPickedFile, clearFile, handleConfirm, handleClose,
  };
}